/*
 * Akomoda — Super Admin Billing Console
 * -----------------------------------------------------------------
 * All billing management for Akomoda SaaS lives in this single file
 * because every sub-page shares state, currency formatting and the
 * category list. The router mounts this once with a `tab` prop.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { formatDate } from "@/lib/format";
import Topbar from "@/components/layout/Topbar";
import {
    LayoutDashboard, DollarSign, Package, Boxes, Users, FileText, Scroll,
    Plus, Trash2, Edit3, Play, Pause, ChevronRight, CheckCircle2, XCircle,
    Building2, Sparkles, Calculator, ArrowRight, AlertTriangle, Copy, RefreshCw,
} from "lucide-react";

// ─── shared helpers ───
const money = (v, cur = "EUR") =>
    new Intl.NumberFormat("en-GB", { style: "currency", currency: cur }).format(
        Number(v || 0),
    );
const shortDate = (iso) => (iso ? formatDate(iso) : "—");

const TABS = [
    { key: "dashboard", label: "Paneli", icon: LayoutDashboard,
      subtitle: "Metrikat kryesore për të gjitha abonimet e hoteleve." },
    { key: "pricing", label: "Versionet e çmimeve", icon: DollarSign,
      subtitle: "Versione çmimesh sipas nivelit të dhomave, me nivele automatike + kuota me çmim të veçantë." },
    { key: "modules", label: "Katalogu i moduleve", icon: Package,
      subtitle: "Çdo modul shtesë që Akomoda shet — bazë, opsional dhe me pagesë." },
    { key: "plans", label: "Planet (Paketa)", icon: Boxes,
      subtitle: "Paketa të emërtuara modulesh me çmim fiks mujor + vjetor." },
    { key: "subscriptions", label: "Abonimet", icon: Users,
      subtitle: "Çdo hotel dhe abonimi i tij aktiv." },
    { key: "invoices", label: "Faturat", icon: FileText,
      subtitle: "Faturat SaaS me shumën e detyruar, statusin e pagesës dhe eksport PDF." },
    { key: "audit", label: "Gjurma e auditimit", icon: Scroll,
      subtitle: "Çdo ndryshim në çmime, module, plane dhe abonime." },
];

export default function SuperBilling({ defaultTab = "dashboard" }) {
    const current = TABS.find((t) => t.key === defaultTab) || TABS[0];

    return (
        <div className="min-h-screen bg-background">
            <Topbar />
            <div className="mx-auto max-w-[1400px] p-6" data-testid="saas-billing-root">
                <div className="mb-6 flex items-baseline justify-between">
                    <div>
                        <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2.5">
                            <current.icon size={26} className="text-[#ff7b02]" /> {current.label}
                        </h1>
                        <p className="mt-1 text-sm text-muted-foreground">{current.subtitle}</p>
                    </div>
                </div>

                {defaultTab === "dashboard" && <BillingDashboard />}
                {defaultTab === "pricing" && <PricingSection />}
                {defaultTab === "modules" && <ModulesSection />}
                {defaultTab === "plans" && <PlansSection />}
                {defaultTab === "subscriptions" && <SubscriptionsSection />}
                {defaultTab === "invoices" && <InvoicesSection />}
                {defaultTab === "audit" && <AuditSection />}
            </div>
        </div>
    );
}

// ═════════════════════════════════════════════════════════════════
// DASHBOARD
// ═════════════════════════════════════════════════════════════════
function BillingDashboard() {
    const [kpis, setKpis] = useState(null);
    const load = async () => {
        try {
            const { data } = await api.get("/saas/dashboard/kpis");
            setKpis(data);
        } catch (e) { toast.error("Failed to load KPIs"); }
    };
    useEffect(() => { load(); }, []);
    if (!kpis) return <SkeletonBlock />;

    const kpiCards = [
        { label: "MRR", value: money(kpis.mrr), icon: DollarSign, hint: "Të ardhura periodike mujore" },
        { label: "ARR", value: money(kpis.arr), icon: DollarSign, hint: "Norma vjetore" },
        { label: "Abonime aktive", value: kpis.total_subscriptions, icon: Users, hint: "Të gjitha statuset" },
        { label: "Dhoma të faturueshme", value: kpis.total_billable_rooms, icon: Building2, hint: "Total dhoma aktive" },
        { label: "Të ardhura mesatare / Hotel", value: money(kpis.avg_revenue_per_hotel), icon: Calculator, hint: "" },
        { label: "Fatura të hapura", value: kpis.open_invoices, icon: FileText, hint: "Në pritje të pagesës" },
        { label: "E papaguar", value: money(kpis.outstanding_amount), icon: AlertTriangle, hint: "Shuma e detyruar" },
    ];
    return (
        <div className="space-y-6" data-testid="billing-dashboard">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3 lg:grid-cols-4">
                {kpiCards.map((k) => (
                    <div key={k.label} className="akm-card p-4">
                        <div className="flex items-center gap-2 text-[11px] uppercase tracking-wide text-muted-foreground">
                            <k.icon size={12} /> {k.label}
                        </div>
                        <div className="mt-2 text-2xl font-bold tracking-tight">{k.value}</div>
                        {k.hint && <div className="mt-1 text-[10px] text-muted-foreground">{k.hint}</div>}
                    </div>
                ))}
            </div>

            {kpis.subscriptions_by_status && (
                <div className="akm-card p-4">
                    <div className="mb-3 text-sm font-semibold">Abonimet sipas statusit</div>
                    <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
                        {Object.entries(kpis.subscriptions_by_status).map(([s, n]) => (
                            <div key={s} className="rounded-md border border-border bg-muted/20 px-3 py-2">
                                <div className="text-[10px] uppercase text-muted-foreground">{s}</div>
                                <div className="text-lg font-bold">{n}</div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

// ═════════════════════════════════════════════════════════════════
// PRICING (versions + tiers + preview)
// ═════════════════════════════════════════════════════════════════
function PricingSection() {
    const [versions, setVersions] = useState([]);
    const [activeVersionId, setActiveVersionId] = useState(null);
    const [tiers, setTiers] = useState([]);
    const [previewRooms, setPreviewRooms] = useState(40);
    const [preview, setPreview] = useState(null);
    const [creatingTier, setCreatingTier] = useState(false);

    const loadVersions = async () => {
        const { data } = await api.get("/saas/pricing/versions");
        setVersions(data);
        const active = data.find((v) => v.status === "active") || data[0];
        if (active) setActiveVersionId(active.id);
    };
    const loadTiers = async (id) => {
        if (!id) return;
        const { data } = await api.get(`/saas/pricing/tiers?pricing_version_id=${id}`);
        setTiers(data);
    };
    const doPreview = async () => {
        if (!activeVersionId) return;
        const { data } = await api.post("/saas/pricing/preview", {
            pricing_version_id: activeVersionId,
            room_count: Number(previewRooms) || 0,
        });
        setPreview(data.breakdown);
    };
    useEffect(() => { loadVersions(); }, []);
    useEffect(() => { loadTiers(activeVersionId); doPreview(); /* eslint-disable-next-line */ }, [activeVersionId]);

    const active = versions.find((v) => v.id === activeVersionId);

    return (
        <div className="space-y-6" data-testid="pricing-section">
            {/* Pricing versions */}
            <div className="akm-card p-4">
                <div className="mb-3 flex items-baseline justify-between">
                    <h2 className="text-sm font-semibold">Versionet e çmimeve</h2>
                    <button className="akm-btn-primary text-[11px]" onClick={async () => {
                        const name = prompt("Emri i versionit:");
                        if (!name) return;
                        const code = prompt("Kodi i versionit (unik):", name.toLowerCase().replace(/\s+/g, "_"));
                        if (!code) return;
                        await api.post("/saas/pricing/versions", { name, code, currency: "EUR", minimum_monthly_charge: "35.00", annual_discount_pct: "15", status: "draft" });
                        toast.success("Versioni u krijua");
                        loadVersions();
                    }} data-testid="new-pricing-version">
                        <Plus size={12} /> Version i ri
                    </button>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-[12px]">
                        <thead className="text-left text-muted-foreground">
                            <tr><th className="pb-2">Emri</th><th>Kodi</th><th>Monedha</th><th>Tarifa min.</th><th>Zbritja vjetore %</th><th>Statusi</th><th>Veprimet</th></tr>
                        </thead>
                        <tbody>
                            {versions.map((v) => (
                                <tr key={v.id} className="border-t border-border" data-testid={`pv-row-${v.code}`}>
                                    <td className="py-2 font-medium">{v.name}</td>
                                    <td className="font-mono text-[11px]">{v.code}</td>
                                    <td>{v.currency}</td>
                                    <td>{money(v.minimum_monthly_charge, v.currency)}</td>
                                    <td>{v.annual_discount_pct}%</td>
                                    <td><StatusPill s={v.status} /></td>
                                    <td>
                                        <button className="akm-btn-ghost text-[10px]" onClick={() => setActiveVersionId(v.id)}>
                                            <Edit3 size={10} /> Menaxho
                                        </button>
                                        {v.status !== "active" && (
                                            <button className="akm-btn-ghost text-[10px] text-emerald-600" onClick={async () => {
                                                await api.patch(`/saas/pricing/versions/${v.id}`, { status: "active" });
                                                toast.success("U aktivizua");
                                                loadVersions();
                                            }} data-testid={`activate-${v.code}`}>
                                                <Play size={10} /> Aktivizo
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Tiers for selected version */}
            {active && (
                <div className="akm-card p-4">
                    <div className="mb-3 flex items-baseline justify-between">
                        <div>
                            <h2 className="text-sm font-semibold">Nivelet e çmimeve për dhoma — {active.name}</h2>
                            <p className="text-[11px] text-muted-foreground">
                                Vëllim i sheshtë: niveli që përfshin numrin e dhomave përcakton çmimin për dhomë për TË GJITHA dhomat.
                            </p>
                        </div>
                        <button className="akm-btn-primary text-[11px]" onClick={() => setCreatingTier(true)}>
                            <Plus size={12} /> Shto nivel
                        </button>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-[12px]">
                            <thead className="text-left text-muted-foreground">
                                <tr><th className="pb-2">Nga</th><th>Deri</th><th>Çmimi / dhomë</th><th>Modaliteti</th><th>Statusi</th><th>Veprimet</th></tr>
                            </thead>
                            <tbody>
                                {tiers.map((t) => (
                                    <TierRow key={t.id} tier={t} onSaved={() => loadTiers(activeVersionId)} />
                                ))}
                                {creatingTier && (
                                    <TierRow tier={{ from_rooms: 1, to_rooms: 10, price_per_room: "4.50", commercial_mode: "automatic", status: "active" }} isNew pricingVersionId={activeVersionId} onSaved={() => { setCreatingTier(false); loadTiers(activeVersionId); }} onCancel={() => setCreatingTier(false)} />
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Preview calculator */}
            <div className="akm-card p-4">
                <h2 className="mb-3 text-sm font-semibold">
                    <Calculator size={14} className="mr-2 inline" />Parashiko llogaritjen
                </h2>
                <div className="flex flex-wrap items-end gap-3">
                    <div>
                        <label className="mb-1 block text-[11px] text-muted-foreground">Numri i dhomave</label>
                        <input type="number" min={0} value={previewRooms} onChange={(e) => setPreviewRooms(e.target.value)}
                               className="akm-input w-32" data-testid="preview-rooms-input" />
                    </div>
                    <button className="akm-btn-primary" onClick={doPreview} data-testid="preview-btn">
                        <RefreshCw size={12} /> Llogarit
                    </button>
                    {preview && (
                        <div className="ml-auto flex flex-col items-end">
                            <div className="text-[11px] text-muted-foreground">Tarifa e platformës</div>
                            <div className="text-2xl font-bold" data-testid="preview-platform-charge">
                                {money(preview.platform_charge, preview.currency)}
                            </div>
                            <div className="text-[11px] text-muted-foreground">
                                {preview.tier
                                    ? `${preview.room_count} dhoma × ${money(preview.tier.price_per_room)} = ${money(preview.room_charge)}, min ${money(preview.minimum_charge)}`
                                    : "S'ka nivel përputhës"}
                            </div>
                            {preview.commercial_mode !== "automatic" && (
                                <div className="mt-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-amber-800">
                                    {preview.commercial_mode.replace("_", " ")}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function TierRow({ tier, isNew = false, pricingVersionId, onSaved, onCancel }) {
    const [t, setT] = useState({ ...tier, to_rooms: tier.to_rooms ?? "" });
    const [editing, setEditing] = useState(isNew);
    const save = async () => {
        const body = {
            from_rooms: Number(t.from_rooms),
            to_rooms: t.to_rooms === "" ? null : Number(t.to_rooms),
            price_per_room: String(t.price_per_room),
            commercial_mode: t.commercial_mode,
            status: t.status,
        };
        if (isNew) {
            await api.post("/saas/pricing/tiers", { ...body, pricing_version_id: pricingVersionId });
        } else {
            await api.patch(`/saas/pricing/tiers/${tier.id}`, body);
        }
        toast.success("Niveli u ruajt");
        setEditing(false);
        onSaved && onSaved();
    };
    const doDelete = async () => {
        if (!confirm("Fshini këtë nivel? (duhet të tërhiqet fillimisht)")) return;
        try {
            await api.delete(`/saas/pricing/tiers/${tier.id}`);
            toast.success("U fshi");
            onSaved && onSaved();
        } catch (e) { toast.error(e.response?.data?.detail || "Dështoi"); }
    };
    if (!editing) {
        return (
            <tr className="border-t border-border" data-testid={`tier-row-${tier.id}`}>
                <td className="py-2">{tier.from_rooms}</td>
                <td>{tier.to_rooms ?? <span className="text-muted-foreground">∞</span>}</td>
                <td>{money(tier.price_per_room)}</td>
                <td><ModePill m={tier.commercial_mode} /></td>
                <td><StatusPill s={tier.status} /></td>
                <td>
                    <button className="akm-btn-ghost text-[10px]" onClick={() => setEditing(true)}><Edit3 size={10} /></button>
                    <button className="akm-btn-ghost text-[10px] text-red-600" onClick={doDelete}><Trash2 size={10} /></button>
                </td>
            </tr>
        );
    }
    return (
        <tr className="border-t border-border bg-muted/20">
            <td className="py-2"><input type="number" className="akm-input w-16 text-[11px]" value={t.from_rooms} onChange={(e) => setT({ ...t, from_rooms: e.target.value })} /></td>
            <td><input type="number" className="akm-input w-16 text-[11px]" placeholder="∞" value={t.to_rooms} onChange={(e) => setT({ ...t, to_rooms: e.target.value })} /></td>
            <td><input type="text" className="akm-input w-20 text-[11px]" value={t.price_per_room} onChange={(e) => setT({ ...t, price_per_room: e.target.value })} /></td>
            <td>
                <select className="akm-input text-[11px]" value={t.commercial_mode} onChange={(e) => setT({ ...t, commercial_mode: e.target.value })}>
                    <option value="automatic">automatic</option>
                    <option value="custom_quote">custom_quote</option>
                    <option value="disabled">disabled</option>
                </select>
            </td>
            <td>
                <select className="akm-input text-[11px]" value={t.status} onChange={(e) => setT({ ...t, status: e.target.value })}>
                    <option value="active">active</option>
                    <option value="draft">draft</option>
                    <option value="retired">retired</option>
                </select>
            </td>
            <td>
                <button className="akm-btn-primary text-[10px]" onClick={save} data-testid="tier-save"><CheckCircle2 size={10} /> Ruaj</button>
                {isNew && <button className="akm-btn-ghost text-[10px]" onClick={onCancel}><XCircle size={10} /></button>}
            </td>
        </tr>
    );
}

// ═════════════════════════════════════════════════════════════════
// MODULES
// ═════════════════════════════════════════════════════════════════
function ModulesSection() {
    const [mods, setMods] = useState([]);
    const [cats, setCats] = useState([]);
    const [filter, setFilter] = useState("");
    const load = async () => {
        const [m, c] = await Promise.all([api.get("/saas/modules"), api.get("/saas/categories")]);
        setMods(m.data); setCats(c.data);
    };
    useEffect(() => { load(); }, []);
    const filtered = useMemo(() => {
        const f = filter.toLowerCase();
        return mods.filter((m) => !f || m.name.toLowerCase().includes(f) || m.key.toLowerCase().includes(f));
    }, [mods, filter]);
    const byCat = useMemo(() => {
        const g = {};
        filtered.forEach((m) => { (g[m.category] = g[m.category] || []).push(m); });
        return g;
    }, [filtered]);
    return (
        <div className="space-y-6" data-testid="modules-section">
            <div className="flex items-center gap-3">
                <input placeholder="Kërko module…" className="akm-input flex-1 max-w-md" value={filter} onChange={(e) => setFilter(e.target.value)} />
                <div className="text-[11px] text-muted-foreground">{filtered.length} nga {mods.length} module</div>
            </div>
            {cats.map((c) => {
                const items = byCat[c.key] || [];
                if (!items.length) return null;
                return (
                    <div key={c.key} className="akm-card p-4">
                        <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide">{c.name}</h3>
                        <div className="grid grid-cols-1 gap-2 md:grid-cols-2 lg:grid-cols-3">
                            {items.map((m) => (
                                <div key={m.key} className="rounded-md border border-border p-3">
                                    <div className="flex items-baseline justify-between">
                                        <div className="text-[13px] font-medium">{m.name}</div>
                                        {m.is_core ? <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[9px] font-semibold uppercase text-emerald-800">Bazë</span> : <span className="rounded-full bg-blue-100 px-2 py-0.5 text-[9px] font-semibold uppercase text-blue-800">Opsional</span>}
                                    </div>
                                    <div className="mt-1 font-mono text-[10px] text-muted-foreground">{m.key}</div>
                                    <p className="mt-1 text-[11px] text-muted-foreground line-clamp-2">{m.description}</p>
                                    <div className="mt-2 flex items-center gap-2 text-[11px]">
                                        <span className="rounded bg-muted px-1.5 py-0.5">{m.billing_type}</span>
                                        {Number(m.default_monthly_price) > 0 && <span>{money(m.default_monthly_price)}/mo</span>}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

// ═════════════════════════════════════════════════════════════════
// PLANS
// ═════════════════════════════════════════════════════════════════
function PlansSection() {
    const [plans, setPlans] = useState([]);
    const load = async () => setPlans((await api.get("/saas/plans")).data);
    useEffect(() => { load(); }, []);
    const createPlan = async () => {
        const name = prompt("Emri i planit:");
        if (!name) return;
        const code = prompt("Kodi i planit:", name.toLowerCase().replace(/\s+/g, "_"));
        if (!code) return;
        await api.post("/saas/plans", { name, code, status: "draft", currency: "EUR" });
        toast.success("Plani u krijua");
        load();
    };
    return (
        <div className="space-y-4" data-testid="plans-section">
            <div className="flex items-baseline justify-between">
                <div>
                    <h2 className="text-sm font-semibold">Planet (paketa marketingu)</h2>
                    <p className="text-[11px] text-muted-foreground">Një plan është një përzgjedhje e paracaktuar modulesh — nuk kërkohet plan që një hotel të ketë abonim.</p>
                </div>
                <button className="akm-btn-primary text-[11px]" onClick={createPlan}><Plus size={12} /> Plan i ri</button>
            </div>
            {plans.length === 0 && (
                <div className="akm-card p-6 text-center text-muted-foreground text-[12px]">
                    Ende s&apos;ka plane. Mund t&apos;i faturoni hotelet drejtpërdrejt hotel-për-hotel pa plane.
                </div>
            )}
            {plans.map((p) => (
                <div key={p.id} className="akm-card p-4">
                    <div className="flex items-baseline justify-between">
                        <div>
                            <div className="text-sm font-semibold">{p.name} <span className="ml-2 font-mono text-[10px] text-muted-foreground">{p.code}</span></div>
                            <div className="text-[11px] text-muted-foreground">{p.description}</div>
                        </div>
                        <StatusPill s={p.status} />
                    </div>
                </div>
            ))}
        </div>
    );
}

// ═════════════════════════════════════════════════════════════════
// SUBSCRIPTIONS (all hotels)
// ═════════════════════════════════════════════════════════════════
function SubscriptionsSection() {
    const [subs, setSubs] = useState([]);
    const [hotels, setHotels] = useState([]);
    const load = async () => {
        const [h, sc] = await Promise.all([
            api.get("/superadmin/hotels"),
            (async () => {
                const rows = [];
                const hList = (await api.get("/superadmin/hotels")).data;
                for (const h of hList) {
                    try {
                        const { data } = await api.get(`/saas/hotels/${h.id}/subscription`);
                        rows.push({ hotel: data.hotel, sub: data.subscription, auto_rooms: data.auto_billable_room_count });
                    } catch (e) { /* skip */ }
                }
                return rows;
            })(),
        ]);
        setHotels(h.data);
        setSubs(sc);
    };
    useEffect(() => { load(); }, []);
    return (
        <div className="space-y-4" data-testid="subscriptions-section">
            <div className="akm-card p-4">
                <table className="w-full text-[12px]">
                    <thead className="text-left text-muted-foreground">
                        <tr><th className="pb-2">Hoteli</th><th>Dhomat</th><th>Cikli</th><th>Statusi</th><th>Platforma</th><th>Modulet</th><th>Periodike</th><th></th></tr>
                    </thead>
                    <tbody>
                        {subs.map((row, i) => (
                            <tr key={i} className="border-t border-border">
                                <td className="py-2">
                                    <Link to={`/admin/hotels/${row.hotel.id}?tab=billing`} className="font-medium text-primary hover:underline">
                                        {row.hotel.name}
                                    </Link>
                                </td>
                                <td>{row.sub?.billable_room_count ?? row.auto_rooms} <span className="text-muted-foreground text-[10px]">(auto {row.auto_rooms})</span></td>
                                <td>{row.sub?.billing_cycle || "—"}</td>
                                <td>{row.sub ? <StatusPill s={row.sub.status} /> : <span className="text-muted-foreground">s&apos;ka abonim</span>}</td>
                                <td>{row.sub ? money(row.sub.platform_charge) : "—"}</td>
                                <td>{row.sub ? money(row.sub.module_total) : "—"}</td>
                                <td className="font-bold">{row.sub ? money(row.sub.recurring_total) : "—"}</td>
                                <td>
                                    <Link to={`/admin/hotels/${row.hotel.id}?tab=billing`} className="akm-btn-ghost text-[10px]">
                                        Menaxho <ChevronRight size={10} />
                                    </Link>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// ═════════════════════════════════════════════════════════════════
// INVOICES
// ═════════════════════════════════════════════════════════════════
function InvoicesSection() {
    const [invoices, setInvoices] = useState([]);
    const load = async () => setInvoices((await api.get("/saas/invoices")).data);
    useEffect(() => { load(); }, []);
    return (
        <div className="akm-card p-4" data-testid="invoices-section">
            <table className="w-full text-[12px]">
                <thead className="text-left text-muted-foreground">
                    <tr><th className="pb-2">#</th><th>Hoteli</th><th>Statusi</th><th>Periudha</th><th>Totali</th><th>E paguar</th><th>E detyruar</th><th>Krijuar</th><th></th></tr>
                </thead>
                <tbody>
                    {invoices.map((i) => (
                        <tr key={i.id} className="border-t border-border">
                            <td className="py-2 font-mono">{i.invoice_number}</td>
                            <td>{i.hotel_name || i.hotel_id}</td>
                            <td><StatusPill s={i.status} /></td>
                            <td className="text-[11px]">{i.billing_period?.cycle}</td>
                            <td className="font-bold">{money(i.total, i.currency)}</td>
                            <td className="text-emerald-700">{money(i.amount_paid, i.currency)}</td>
                            <td className="text-red-700">{money(i.amount_due, i.currency)}</td>
                            <td className="text-[11px] text-muted-foreground">{shortDate(i.created_at)}</td>
                            <td>
                                {i.status !== "paid" && (
                                    <div className="flex gap-1">
                                        <button className="akm-btn-ghost text-[10px] text-blue-600"
                                                onClick={async () => {
                                                    if (!confirm(`Tarifoni ${i.hotel_name || i.hotel_id} për ${money(i.amount_due, i.currency)} përmes Stripe?`)) return;
                                                    try {
                                                        const { data } = await api.post(`/saas/invoices/${i.id}/charge`);
                                                        if (data.ok) toast.success(data.already_paid ? "Tashmë e paguar" : "U tarifua me sukses");
                                                        else toast.error(data.message || data.error || "Tarifimi dështoi");
                                                        load();
                                                    } catch (e) { toast.error(e.response?.data?.detail || "Tarifimi dështoi"); }
                                                }} data-testid={`invoice-charge-${i.invoice_number}`}>
                                            <DollarSign size={10} /> Tarifo
                                        </button>
                                        <button className="akm-btn-ghost text-[10px] text-emerald-600"
                                                onClick={async () => {
                                                    if (!confirm(`Shëno faturën ${i.invoice_number} si të paguar (${money(i.amount_due, i.currency)})?`)) return;
                                                    await api.post(`/saas/invoices/${i.id}/mark-paid`, { amount: i.amount_due, method: "manual" });
                                                    toast.success("U shënua e paguar");
                                                    load();
                                                }} data-testid={`invoice-pay-${i.invoice_number}`}>
                                            <CheckCircle2 size={10} /> Shëno të paguar
                                        </button>
                                    </div>
                                )}
                            </td>
                        </tr>
                    ))}
                    {invoices.length === 0 && (
                        <tr><td colSpan={9} className="py-6 text-center text-muted-foreground">Ende s&apos;ka fatura</td></tr>
                    )}
                </tbody>
            </table>
        </div>
    );
}

// ═════════════════════════════════════════════════════════════════
// AUDIT
// ═════════════════════════════════════════════════════════════════
function AuditSection() {
    const [rows, setRows] = useState([]);
    useEffect(() => {
        api.get("/saas/audit?limit=100").then(({ data }) => setRows(data));
    }, []);
    return (
        <div className="akm-card p-4" data-testid="audit-section">
            <table className="w-full text-[12px]">
                <thead className="text-left text-muted-foreground">
                    <tr><th className="pb-2">Kur</th><th>Aktori</th><th>Veprimi</th><th>Burimi</th><th>Metadata</th></tr>
                </thead>
                <tbody>
                    {rows.map((r) => (
                        <tr key={r.id} className="border-t border-border">
                            <td className="py-1 text-[10px] text-muted-foreground">{shortDate(r.created_at)}</td>
                            <td className="text-[11px]">{r.user_email}</td>
                            <td className="font-mono text-[10px]">{r.action}</td>
                            <td>{r.resource}</td>
                            <td className="max-w-md truncate font-mono text-[10px] text-muted-foreground">{JSON.stringify(r.metadata)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

// ═════════════════════════════════════════════════════════════════
// helpers
// ═════════════════════════════════════════════════════════════════
function StatusPill({ s }) {
    const colors = {
        active: "bg-emerald-100 text-emerald-800",
        enabled: "bg-emerald-100 text-emerald-800",
        trial: "bg-blue-100 text-blue-800",
        draft: "bg-slate-100 text-slate-800",
        scheduled: "bg-amber-100 text-amber-800",
        retired: "bg-slate-200 text-slate-600",
        disabled: "bg-red-100 text-red-800",
        cancelled: "bg-red-100 text-red-800",
        past_due: "bg-red-100 text-red-800",
        grace_period: "bg-amber-100 text-amber-800",
        suspended: "bg-red-100 text-red-800",
        paid: "bg-emerald-100 text-emerald-800",
        open: "bg-amber-100 text-amber-800",
        partially_paid: "bg-blue-100 text-blue-800",
    };
    const labels = {
        active: "aktiv", enabled: "aktiv", trial: "provë", draft: "draft",
        scheduled: "planifikuar", retired: "tërhequr", disabled: "çaktivizuar",
        cancelled: "anuluar", past_due: "vonuar", grace_period: "afat mëshirë",
        suspended: "pezulluar", paid: "paguar", open: "e hapur",
        partially_paid: "pjesërisht",
    };
    return <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${colors[s] || "bg-muted"}`}>{labels[s] || s}</span>;
}
function ModePill({ m }) {
    const colors = { automatic: "bg-emerald-100 text-emerald-800", custom_quote: "bg-amber-100 text-amber-800", disabled: "bg-red-100 text-red-800" };
    const labels = { automatic: "automatik", custom_quote: "kuotë e veçantë", disabled: "çaktivizuar" };
    return <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${colors[m] || "bg-muted"}`}>{labels[m] || m.replace("_", " ")}</span>;
}
function SkeletonBlock() {
    return <div className="akm-card animate-pulse p-8" style={{ minHeight: 200 }} />;
}
